<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import TutorApplicationStatus from "@/components/TutorApplicationStatus.vue";
import StatusHeader from "@/components/StatusHeader.vue";
import { logError } from "@/utils/secureLogger";

onMounted(() => {
  document.title = "ElevatED - Tutor Application Status";
});

const router = useRouter();

// State
const isLoading = ref(true);
const error = ref("");
const applicationData = ref<any>(null);

// Methods
const fetchApplicationStatus = async () => {
  isLoading.value = true;
  error.value = "";

  try {
    // Import Firebase functions
    const { auth, db, doc, getDoc, onAuthStateChanged } = await import("@/firebase");

    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      // Wait for auth state to load
      await new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      });

      if (!auth.currentUser) {
        router.push("/login");
        return;
      }
    }

    const user = auth.currentUser!;

    // Get user data from Firestore
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      error.value = "User profile not found.";
      return;
    }

    const userData = userDoc.data();

    // Check if user is a tutor
    if (userData.userType !== "tutor") {
      error.value = "This page is only accessible to tutor applicants.";
      return;
    }

    // If tutor is approved, redirect to dashboard
    if (userData.applicationStatus === "approved" && userData.profileStatus === "approved") {
      router.push("/tutor/sessions");
      return;
    }

    // Set application data for the component
    applicationData.value = {
      user_type: userData.userType,
      application_status: userData.applicationStatus || "pending",
      profile_status: userData.profileStatus || "pending_approval",
      rejection_reason: userData.rejectionReason || "",
      application_submitted_at: userData.applicationDate,
      application_reviewed_at: userData.reviewedAt,
      first_name: userData.firstName || "",
      last_name: userData.lastName || "",
      email: userData.email || "",
      subjects: userData.subjects || [],
      experience_years: userData.experienceYears || 0,
      bio: userData.bio || "",
    };
  } catch (err: any) {
    logError("Error fetching application status:", err);
    error.value = err.message || "Failed to load application status. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const handleStatusUpdate = (data: any) => {
  applicationData.value = data;

  // If status changed to approved, redirect to dashboard
  if (data.application_status === "approved") {
    router.push("/tutor/sessions");
  }
};

// Lifecycle
onMounted(() => {
  fetchApplicationStatus();
});
</script>

<template>
  <div class="tutor-app-status-page">
    <!-- Loading State -->
    <div v-if="isLoading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
        ></div>
        <p class="text-gray-600">Loading your application status...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="min-h-screen flex items-center justify-center px-4">
      <div
        class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center border-t-4 border-red-500"
      >
        <div
          class="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4"
        >
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-800 mb-2">Error Loading Status</h2>
        <p class="text-gray-600 mb-6">{{ error }}</p>
        <button
          @click="fetchApplicationStatus"
          class="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>

    <!-- Application Status Component -->
    <div v-else-if="applicationData">
      <StatusHeader />
      <TutorApplicationStatus
        :application-status="applicationData.application_status"
        :rejection-reason="applicationData.rejection_reason"
        :application-submitted-at="applicationData.application_submitted_at"
        :application-reviewed-at="applicationData.application_reviewed_at"
        @status-updated="handleStatusUpdate"
      />
    </div>

    <!-- Fallback for non-tutor users -->
    <div v-else class="min-h-screen flex items-center justify-center px-4">
      <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <h2 class="text-xl font-semibold text-gray-800 mb-4">Access Denied</h2>
        <p class="text-gray-600 mb-6">This page is only accessible to tutor applicants.</p>
        <button
          @click="$router.push('/')"
          class="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Tutor Dashboard Background */
.tutor-app-status-page {
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  min-height: 100vh;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
